import { RefreshCwIcon } from "lucide-react";
import { forwardRef } from "react";

import { getDonorIds } from "@blis/shared/models/subject";

import { StatIndicator } from "@/components/Indicators";
import { Badge } from "@/components/ui/badge";
import {
  ListItem,
  ListItemLeft,
  ListItemRight,
} from "@/components/ui/list-item";
import { Text } from "@/components/ui/text";
import { cn } from "@/components/ui/utils";
import type { WorklistNew } from "../types";

export const WorklistAccessionItem = forwardRef<
  HTMLDivElement,
  { order: WorklistNew["testOrders"][number]; className?: string }
>(({ order, ...props }, ref) => {
  let status = "Pending";
  let color: "gray" | "blue" | "green" = "gray";
  if (order.cancelled) {
    status = "Cancelled";
    color = "gray";
  } else if (order.results.some((res) => res.status === "initial")) {
    status = "Validating";
    color = "blue";
  } else if (
    order.results.every(
      (res) => res.status === "validated" || res.status === "final",
    )
  ) {
    status = "Complete";
    color = "green";
  }

  const accession = order.accession;

  const ids = getDonorIds(accession.subject);

  return (
    <ListItem
      ref={ref}
      {...props}
      className={cn(
        "cursor-pointer hover:bg-muted/50 data-[selected=true]:bg-muted data-[selected=true]:hover:bg-muted",
        props.className,
      )}
    >
      <ListItemLeft>
        {order.samples.map((sample) => (
          <Badge
            variant="outline"
            key={sample.sampleId}
            className="tabular-nums whitespace-nowrap"
          >
            {sample.sample.displayId}
          </Badge>
        ))}
        {order.samples.length === 0 && (
          <Badge variant="outline">No sample</Badge>
        )}

        <Text
          className={cn(
            order.cancelled && "line-through",
            "text-xs sm:text-sm",
          )}
          truncate
        >
          {ids.caseId}
        </Text>
        {order.isReflex && (
          <RefreshCwIcon className="text-muted-foreground size-3" />
        )}
        {accession.priority === "STAT" && <StatIndicator />}
      </ListItemLeft>
      <ListItemRight>
        <Text muted className="tabular-nums hidden md:inline">
          {order.accessionId}
        </Text>
        <Badge color={color}>{status}</Badge>
      </ListItemRight>
    </ListItem>
  );
});

WorklistAccessionItem.displayName = "WorklistAccessionItem";
